import React, { useState, useEffect, useRef } from "react";
import { cmtsApi } from "../../services/api.service.js";

export interface EmailTemplateItem {
    id: string;
    tenantId?: string;
    code: string;
    eventType: string;
    channel: string;
    subjectTemplate: string;
    bodyTemplate: string;
    name?: string;
    maxRetryCount?: number;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
}

const AVAILABLE_VARIABLES = [
    { key: "{{ComplaintCode}}", label: "Complaint Code", sample: "CMP-2026-000042" },
    { key: "{{Title}}", label: "Title", sample: "Dimensional non-conformance in batch #882" },
    { key: "{{Description}}", label: "Description", sample: "Critical tolerance deviation detected during quality inspection." },
    { key: "{{Severity}}", label: "Severity", sample: "Critical" },
    { key: "{{Priority}}", label: "Priority", sample: "High" },
    { key: "{{CreatedBy}}", label: "Created By", sample: "Ramesh Kumar (Quality Lead)" },
    { key: "{{CreatedAt}}", label: "Created Date", sample: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " UTC" },
    { key: "{{AppUrl}}", label: "App URL", sample: "http://localhost:3000" },
    { key: "{{ComplaintId}}", label: "Complaint ID", sample: "c7e8412e-9d2a-41f2-8924-f1837a2b5311" }
];

const DEFAULT_HTML_TEMPLATE = `<h3>New Complaint Notification</h3>
<p>Dear Platform Admin,</p>
<p>A new complaint has been registered in the CTMS platform:</p>
<table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;max-width:600px;margin:16px 0;font-family:Arial,sans-serif;">
  <tr style="background-color:#f3f4f6;">
    <td style="width:35%;"><strong>Complaint Code</strong></td>
    <td>{{ComplaintCode}}</td>
  </tr>
  <tr>
    <td><strong>Title</strong></td>
    <td>{{Title}}</td>
  </tr>
  <tr>
    <td><strong>Description</strong></td>
    <td>{{Description}}</td>
  </tr>
  <tr>
    <td><strong>Severity</strong></td>
    <td><span style="color:#dc2626;font-weight:bold;">{{Severity}}</span></td>
  </tr>
  <tr>
    <td><strong>Priority</strong></td>
    <td>{{Priority}}</td>
  </tr>
  <tr>
    <td><strong>Created By</strong></td>
    <td>{{CreatedBy}}</td>
  </tr>
  <tr>
    <td><strong>Date & Time</strong></td>
    <td>{{CreatedAt}}</td>
  </tr>
</table>
<p style="margin-top:20px;">
  <a href="{{AppUrl}}/complaints/{{ComplaintId}}" style="display:inline-block;padding:10px 20px;background-color:#1a56db;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">View Complaint Details</a>
</p>
<p style="color:#6b7280;font-size:12px;margin-top:30px;">This is an automated notification from CTMS. Please do not reply directly to this email.</p>`;

export function EmailTemplateEditorModal(props: {
    readonly isOpen: boolean;
    readonly template: EmailTemplateItem | null;
    readonly onClose: () => void;
    readonly onSave: (template: Partial<EmailTemplateItem>) => Promise<void> | void;
}) {
    const [name, setName] = useState("");
    const [code, setCode] = useState("NTF_COMPLAINT_CREATED_EMAIL");
    const [eventType, setEventType] = useState("COMPLAINT_CREATED");
    const [subject, setSubject] = useState("[CTMS Alert] New Complaint Registered - {{ComplaintCode}}");
    const [body, setBody] = useState(DEFAULT_HTML_TEMPLATE);
    const [editorTab, setEditorTab] = useState<"visual" | "html">("visual");
    const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
    const [isSaving, setIsSaving] = useState(false);
    const [lastFocusedInput, setLastFocusedInput] = useState<"subject" | "body">("body");

    const visualEditorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (props.template) {
            setName(props.template.name ?? props.template.code.replace(/_/g, " "));
            setCode(props.template.code);
            setEventType(props.template.eventType || "COMPLAINT_CREATED");
            setSubject(props.template.subjectTemplate || "");
            setBody(props.template.bodyTemplate || DEFAULT_HTML_TEMPLATE);
        } else {
            setName("Complaint Created Platform Admin Alert");
            setCode("NTF_COMPLAINT_CREATED_EMAIL");
            setEventType("COMPLAINT_CREATED");
            setSubject("[CTMS Alert] New Complaint Registered - {{ComplaintCode}}");
            setBody(DEFAULT_HTML_TEMPLATE);
        }
    }, [props.template, props.isOpen]);

    useEffect(() => {
        if (visualEditorRef.current && editorTab === "visual") {
            if (visualEditorRef.current.innerHTML !== body) {
                visualEditorRef.current.innerHTML = body;
            }
        }
    }, [body, editorTab]);

    if (!props.isOpen) return null;

    const insertVariable = (variableKey: string) => {
        if (lastFocusedInput === "subject") {
            setSubject((prev) => prev + " " + variableKey);
        } else {
            if (editorTab === "visual" && visualEditorRef.current) {
                document.execCommand("insertText", false, variableKey);
                setBody(visualEditorRef.current.innerHTML);
            } else {
                setBody((prev) => prev + " " + variableKey);
            }
        }
    };

    const handleExecCommand = (command: string, value: string | undefined = undefined) => {
        document.execCommand(command, false, value);
        if (visualEditorRef.current) {
            setBody(visualEditorRef.current.innerHTML);
        }
    };

    // Generate live preview with replaced variables
    const renderPreview = (content: string) => {
        let result = content;
        AVAILABLE_VARIABLES.forEach((v) => {
            const regex = new RegExp(v.key.replace(/([{}])/g, "\\$1"), "gi");
            result = result.replace(regex, v.sample);
        });
        return result;
    };

    const previewSubject = renderPreview(subject);
    const previewBody = renderPreview(body);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await props.onSave({
                id: props.template?.id,
                code: code.trim().toUpperCase(),
                eventType: eventType.trim().toUpperCase(),
                channel: "EMAIL",
                name: name.trim(),
                subjectTemplate: subject.trim(),
                bodyTemplate: body.trim(),
                isActive: true,
            });
            props.onClose();
        } catch (err) {
            console.error("Failed to save email template:", err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh] animate-in fade-in zoom-in-95 duration-200">

                {/* Modal Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white flex items-center justify-between shadow-md">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-300">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white tracking-wide">
                                {props.template ? "Edit Email Template" : "Create New Email Template"}
                            </h2>
                            <p className="text-xs text-blue-200/80">
                                Compose email subject & content, customize recipients, and save into database for instant or future use.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={props.onClose}
                        className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                    >
                        ✕
                    </button>
                </div>

                {/* Modal Body: 2 Columns Grid */}
                <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 overflow-y-auto bg-slate-50">

                    {/* Left Column: Form & Editor (7 cols) */}
                    <div className="lg:col-span-7 flex flex-col gap-4">

                        {/* Top Row: Template Name & Unique Code */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                                    Template Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Complaint Creation Admin Alert"
                                    className="w-full px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all shadow-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                                    Template Code (Unique DB Identifier) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="e.g. NTF_COMPLAINT_CREATED_EMAIL"
                                    className="w-full px-3.5 py-2 text-sm font-mono bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Email Subject Line */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                                Email Subject Line <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                value={subject}
                                onFocus={() => setLastFocusedInput("subject")}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="e.g. [CTMS Alert] New Complaint Registered - {{ComplaintCode}}"
                                className="w-full px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 outline-none transition-all shadow-sm font-medium"
                            />
                        </div>

                        {/* Dynamic Variables Pill Bar */}
                        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5 uppercase tracking-wide">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Dynamic Variables (Click to Insert)
                                </span>
                                <span className="text-[11px] text-slate-500 font-medium">Auto-replaced per recipient</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {AVAILABLE_VARIABLES.map((v) => (
                                    <button
                                        key={v.key}
                                        type="button"
                                        onClick={() => insertVariable(v.key)}
                                        title={`Insert ${v.label} (Sample: ${v.sample})`}
                                        className="px-2.5 py-1 text-xs font-mono font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all shadow-2xs cursor-pointer active:scale-95"
                                    >
                                        {v.key}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Editor Container with Tabs */}
                        <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-h-[300px]">

                            {/* Editor Tabs & Toolbar Header */}
                            <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">

                                {/* Tab Switcher */}
                                <div className="flex items-center bg-slate-200/80 p-0.5 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (editorTab === "html" && visualEditorRef.current) {
                                                visualEditorRef.current.innerHTML = body;
                                            }
                                            setEditorTab("visual");
                                        }}
                                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${editorTab === "visual"
                                                ? "bg-blue-900 text-white shadow-sm"
                                                : "text-slate-700 hover:text-slate-900"
                                            }`}
                                    >
                                        Visual Rich Editor
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditorTab("html")}
                                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${editorTab === "html"
                                                ? "bg-blue-900 text-white shadow-sm"
                                                : "text-slate-700 hover:text-slate-900"
                                            }`}
                                    >
                                        &lt;&gt; HTML Source Code
                                    </button>
                                </div>

                                {/* Formatting Tools for Visual Mode */}
                                {editorTab === "visual" && (
                                    <div className="flex items-center gap-1 text-slate-700">
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("bold")}
                                            title="Bold (Ctrl+B)"
                                            className="w-7 h-7 flex items-center justify-center font-bold text-xs rounded hover:bg-slate-200 border border-transparent hover:border-slate-300"
                                        >
                                            B
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("italic")}
                                            title="Italic (Ctrl+I)"
                                            className="w-7 h-7 flex items-center justify-center italic text-xs rounded hover:bg-slate-200 border border-transparent hover:border-slate-300"
                                        >
                                            I
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("underline")}
                                            title="Underline (Ctrl+U)"
                                            className="w-7 h-7 flex items-center justify-center underline text-xs rounded hover:bg-slate-200 border border-transparent hover:border-slate-300"
                                        >
                                            U
                                        </button>
                                        <span className="h-4 w-px bg-slate-300 mx-1" />
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("insertUnorderedList")}
                                            title="Bullet List"
                                            className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-slate-200 border border-transparent hover:border-slate-300"
                                        >
                                            •≡
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("insertOrderedList")}
                                            title="Numbered List"
                                            className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-slate-200 border border-transparent hover:border-slate-300"
                                        >
                                            1≡
                                        </button>
                                        <span className="h-4 w-px bg-slate-300 mx-1" />
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("justifyLeft")}
                                            title="Align Left"
                                            className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-slate-200"
                                        >
                                            ⇤
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleExecCommand("justifyCenter")}
                                            title="Align Center"
                                            className="w-7 h-7 flex items-center justify-center text-xs rounded hover:bg-slate-200"
                                        >
                                            ≡
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Editor Workspace */}
                            <div className="flex-1 p-3">
                                {editorTab === "visual" ? (
                                    <div
                                        ref={visualEditorRef}
                                        contentEditable
                                        onFocus={() => setLastFocusedInput("body")}
                                        onInput={(e) => setBody((e.target as HTMLDivElement).innerHTML)}
                                        className="w-full h-full min-h-[220px] p-3 text-sm text-slate-800 outline-none overflow-y-auto bg-white rounded focus:ring-1 focus:ring-blue-500"
                                        style={{ minHeight: "240px" }}
                                    />
                                ) : (
                                    <textarea
                                        value={body}
                                        onFocus={() => setLastFocusedInput("body")}
                                        onChange={(e) => setBody(e.target.value)}
                                        rows={12}
                                        className="w-full h-full p-3 font-mono text-xs text-slate-900 bg-slate-900 text-slate-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                        placeholder="Enter raw HTML email template code..."
                                    />
                                )}
                            </div>
                        </div>

                        {/* Bottom Actions for Form */}
                        <div className="flex items-center justify-between pt-2">
                            <button
                                type="button"
                                onClick={props.onClose}
                                className="px-5 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-100 transition-all shadow-xs"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={isSaving}
                                className="px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                </svg>
                                {isSaving ? "Saving in Database..." : "Save Template In Database"}
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Live Recipient Email Preview (5 cols) */}
                    <div className="lg:col-span-5 flex flex-col bg-white rounded-2xl border border-slate-200/90 shadow-md p-4 overflow-hidden">

                        {/* Preview Header with Desktop/Mobile Switcher */}
                        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                            <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                                👁️ Live Recipient Email Preview
                            </span>
                            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => setPreviewDevice("desktop")}
                                    className={`px-2.5 py-1 text-xs font-semibold rounded-md flex items-center gap-1 transition-all ${previewDevice === "desktop"
                                            ? "bg-white text-blue-700 shadow-xs border border-slate-200"
                                            : "text-slate-600 hover:text-slate-900"
                                        }`}
                                >
                                    💻 Desktop
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPreviewDevice("mobile")}
                                    className={`px-2.5 py-1 text-xs font-semibold rounded-md flex items-center gap-1 transition-all ${previewDevice === "mobile"
                                            ? "bg-white text-blue-700 shadow-xs border border-slate-200"
                                            : "text-slate-600 hover:text-slate-900"
                                        }`}
                                >
                                    📱 Mobile
                                </button>
                            </div>
                        </div>

                        {/* Email Preview Frame Container */}
                        <div className="flex-1 flex justify-center items-start pt-4 overflow-y-auto bg-slate-100/60 rounded-xl mt-3 p-3">
                            <div
                                className={`bg-white rounded-xl shadow-lg border border-slate-200 flex flex-col transition-all duration-300 ${previewDevice === "mobile" ? "w-[340px] max-w-full" : "w-full"
                                    }`}
                            >
                                {/* Email Client Header Details */}
                                <div className="p-3.5 border-b border-slate-100 bg-slate-50/70 rounded-t-xl text-xs space-y-1">
                                    <div className="flex items-center justify-between text-slate-500 text-[11px]">
                                        <span>From: <strong>CTMS Notification &lt;noreply@ctms.com&gt;</strong></span>
                                        <span>Today</span>
                                    </div>
                                    <div className="text-slate-700 text-[11px]">
                                        To: <strong>Platform Admin &lt;admin@swotdiginxt.com&gt;</strong>
                                    </div>
                                    <div className="font-bold text-slate-900 text-sm pt-1 border-t border-slate-200/60 mt-1">
                                        {previewSubject || "No Subject"}
                                    </div>
                                </div>

                                {/* Email Body Content */}
                                <div className="p-4 overflow-x-auto text-sm text-slate-800 prose prose-sm max-w-none">
                                    <div dangerouslySetInnerHTML={{ __html: previewBody }} />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
}

export function EmailTemplateManager() {
    const [templates, setTemplates] = useState<EmailTemplateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateItem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const res = await cmtsApi.get("/api/cmts/email-templates?channel=EMAIL");
            const list = res.data?.data || res.data || [];
            if (Array.isArray(list)) {
                setTemplates(list);
            }
        } catch (err) {
            console.error("Error fetching templates:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleSave = async (payload: Partial<EmailTemplateItem>) => {
        try {
            const isEdit = Boolean(payload.id);
            const url = isEdit ? `/api/cmts/email-templates/${payload.id}` : "/api/cmts/email-templates";
            const res = isEdit ? await cmtsApi.put(url, payload) : await cmtsApi.post(url, payload);

            if (res.status >= 200 && res.status < 300) {
                setSuccessMessage(isEdit ? "Email template updated in database!" : "New email template saved in database!");
                setTimeout(() => setSuccessMessage(null), 4000);
                await fetchTemplates();
            }
        } catch (err) {
            console.error("Error saving template:", err);
        }
    };

    const handleDeactivate = async (id: string) => {
        if (!window.confirm("Are you sure you want to deactivate this email template?")) return;
        try {
            const res = await cmtsApi.delete(`/api/cmts/email-templates/${id}`);
            if (res.status >= 200 && res.status < 300) {
                await fetchTemplates();
            }
        } catch (err) {
            console.error("Error deactivating template:", err);
        }
    };

    const filtered = templates.filter((t) => {
        const s = searchTerm.toLowerCase();
        return t.code.toLowerCase().includes(s) || (t.name && t.name.toLowerCase().includes(s)) || t.subjectTemplate.toLowerCase().includes(s);
    });

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">

            {/* Page Title & Action Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
                        <span className="p-2 rounded-xl bg-blue-100 text-blue-800">
                            ✉️
                        </span>
                        Email Templates Management
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Manage automated notification email templates stored in database with live dynamic previews and custom tags.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        setSelectedTemplate(null);
                        setIsModalOpen(true);
                    }}
                    className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer active:scale-95"
                >
                    <span>＋</span> Create New Email Template
                </button>
            </div>

            {/* Success Notification Alert */}
            {successMessage && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold flex items-center justify-between animate-in fade-in">
                    <span>✓ {successMessage}</span>
                    <button type="button" onClick={() => setSuccessMessage(null)} className="text-emerald-600 font-bold">✕</button>
                </div>
            )}

            {/* Filters & Search */}
            <div className="flex items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                <div className="relative w-full max-w-md">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search templates by code, subject, or name..."
                        className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-600 outline-none"
                    />
                    <span className="absolute left-3 top-2.5 text-slate-400 text-sm">🔍</span>
                </div>
                <button
                    type="button"
                    onClick={fetchTemplates}
                    className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
                >
                    ↻ Refresh
                </button>
            </div>

            {/* Templates Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-700">
                        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-bold text-slate-500 tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Template Identifier & Code</th>
                                <th className="px-6 py-4">Event Trigger</th>
                                <th className="px-6 py-4">Channel</th>
                                <th className="px-6 py-4">Subject Line</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                        Loading templates from database...
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                        No email templates found in database. Click "Create New Email Template" to add one!
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((t) => (
                                    <tr key={t.id} className="hover:bg-slate-50/80 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-900">{t.name ?? t.code}</div>
                                            <div className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded inline-block mt-0.5">
                                                {t.code}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-mono text-xs text-slate-600 font-semibold">
                                            {t.eventType}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                                {t.channel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 max-w-xs truncate text-slate-800 font-medium">
                                            {t.subjectTemplate}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`px-2.5 py-1 text-xs font-bold rounded-full ${t.isActive
                                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                        : "bg-slate-100 text-slate-500"
                                                    }`}
                                            >
                                                {t.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedTemplate(t);
                                                    setIsModalOpen(true);
                                                }}
                                                className="px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-600 hover:text-white rounded-lg transition-all"
                                            >
                                                Edit & Preview
                                            </button>
                                            {t.isActive && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeactivate(t.id)}
                                                    className="px-3 py-1.5 text-xs font-bold text-red-700 bg-red-50 hover:bg-red-600 hover:text-white rounded-lg transition-all"
                                                >
                                                    Deactivate
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Editor Modal */}
            <EmailTemplateEditorModal
                isOpen={isModalOpen}
                template={selectedTemplate}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedTemplate(null);
                }}
                onSave={handleSave}
            />

        </div>
    );
}
